import { useEffect, useRef, useState } from 'react';

/**
 * SectionMediaBackdrop — generalized full-bleed backdrop for non-hero sections.
 *
 * Generalizes HeroVideoBackdrop so any "billboard" section can ship with a
 * photo or video background and the same brand-safe scrim treatment.
 *
 * Layers (back to front):
 *   1. <picture>/<img> for image, or <video> for video (poster fallback under
 *      reduced-motion or before the intersection observer fires)
 *   2. Vertical/diagonal scrim — protects foreground text contrast
 *   3. Optional faint jade radial — only on `intensity: 'heavy'` to echo hero
 *
 * Intensity scales the scrim strength:
 *   - light:  subtle veil — for photos that are already dim
 *   - medium: default — most billboard sections
 *   - heavy:  for busy / high-contrast photos and video CTAs
 *
 * Focal lets the caller anchor the cropped subject (default 'center'). On
 * mobile, busy photos with portrait subjects look better with `focal='top'`.
 *
 * For videos, the same intersection-observer pause/resume + reduced-motion
 * guard from HeroVideoBackdrop is in play.
 */
export function SectionMediaBackdrop({
  type = 'image',
  src,
  webp,
  poster,
  videoSources,
  isDarkMode,
  intensity = 'medium',
  focal = 'center',
  alt = '',
  keepPoster = false,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [shouldRenderVideo, setShouldRenderVideo] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (type !== 'video') return;
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (reducedMotion) return;
    if (!keepPoster) {
      setShouldRenderVideo(true);
      return;
    }

    // Let the hero poster and headline paint before video fetch/decoding can
    // compete for bandwidth and main-thread time. The poster stays mounted.
    let timer;
    const activate = () => {
      timer = window.setTimeout(() => setShouldRenderVideo(true), 1500);
    };
    if (document.readyState === 'complete') activate();
    else window.addEventListener('load', activate, { once: true });
    return () => {
      window.removeEventListener('load', activate);
      window.clearTimeout(timer);
    };
  }, [type, keepPoster]);

  useEffect(() => {
    if (!shouldRenderVideo) return;
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [shouldRenderVideo]);

  const objectPositionClass =
    typeof focal === 'string' && focal.startsWith('object-')
      ? focal
      : focalClass(focal);

  return (
    <div
      ref={containerRef}
      aria-hidden='true'
      className='absolute inset-0 overflow-hidden pointer-events-none'
    >
      {type === 'video' && keepPoster && (
        <img
          src={poster}
          alt={alt}
          fetchpriority='high'
          decoding='async'
          className={`absolute inset-0 w-full h-full object-cover ${objectPositionClass}`}
        />
      )}
      {type === 'video' ? (
        shouldRenderVideo ? (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload='none'
            poster={poster}
            onError={keepPoster ? (event) => { event.currentTarget.style.visibility = 'hidden'; } : undefined}
            className={`absolute inset-0 w-full h-full object-cover ${objectPositionClass}`}
          >
            {videoSources?.map((s) => (
              <source key={s.src} src={s.src} type={s.type} media={s.media} />
            ))}
          </video>
        ) : !keepPoster ? (
          <img
            src={poster}
            alt={alt}
            className={`absolute inset-0 w-full h-full object-cover ${objectPositionClass}`}
          />
        ) : null
      ) : (
        <picture>
          {webp && <source srcSet={webp} type='image/webp' />}
          <img
            src={src}
            alt={alt}
            className={`absolute inset-0 w-full h-full object-cover ${objectPositionClass}`}
            loading='lazy'
            decoding='async'
          />
        </picture>
      )}

      {/* Scrim — keeps text legible on top of any photo or frame */}
      {isDarkMode ? (
        <>
          <div
            className={`absolute inset-0 bg-gradient-to-r ${SCRIM_DARK_X[intensity]}`}
          />
          <div
            className={`absolute inset-0 bg-gradient-to-b ${SCRIM_DARK_Y[intensity]}`}
          />
          {intensity === 'heavy' && (
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_25%_45%,rgba(0,168,107,0.15)_0%,transparent_60%)]' />
          )}
        </>
      ) : (
        <>
          <div
            className={`absolute inset-0 bg-gradient-to-r ${SCRIM_LIGHT_X[intensity]}`}
          />
          <div
            className={`absolute inset-0 bg-gradient-to-b ${SCRIM_LIGHT_Y[intensity]}`}
          />
          {intensity === 'heavy' && (
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_25%_45%,rgba(0,107,69,0.10)_0%,transparent_60%)]' />
          )}
        </>
      )}
    </div>
  );
}

const SCRIM_DARK_X = {
  light: 'from-zinc-950/60 via-zinc-950/30 to-zinc-950/10',
  medium: 'from-zinc-950/80 via-zinc-950/45 to-zinc-950/15',
  heavy: 'from-zinc-950/90 via-zinc-950/55 to-zinc-950/20',
};

const SCRIM_DARK_Y = {
  light: 'from-transparent via-transparent to-zinc-950/30',
  medium: 'from-transparent via-transparent to-zinc-950/45',
  heavy: 'from-transparent via-transparent to-zinc-950/60',
};

const SCRIM_LIGHT_X = {
  light: 'from-white/60 via-white/30 to-white/10',
  medium: 'from-white/80 via-white/45 to-white/15',
  heavy: 'from-white/90 via-white/55 to-white/20',
};

const SCRIM_LIGHT_Y = {
  light: 'from-transparent via-transparent to-white/25',
  medium: 'from-transparent via-transparent to-white/35',
  heavy: 'from-transparent via-transparent to-white/45',
};

function focalClass(focal) {
  switch (focal) {
    case 'top':
      return 'object-top';
    case 'bottom':
      return 'object-bottom';
    case 'left':
      return 'object-left';
    case 'right':
      return 'object-right';
    case 'center':
    default:
      return 'object-center';
  }
}

export default SectionMediaBackdrop;
